import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./motionPrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = ["Marketing", "SEO", "AI Integration", "Lead Generation"];
const BUDGETS = [
    "Under $2,500 / month",
    "$2,500 – $5,000 / month",
    "$5,000 – $15,000 / month",
    "$15,000+ / month",
    "Not sure yet",
];

const inputCls =
    "w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-mist/50 outline-none focus:border-gold transition-colors";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        services: [],
        budget: "",
        message: "",
    });
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const toggleService = (s) =>
        set(
            "services",
            form.services.includes(s)
                ? form.services.filter((x) => x !== s)
                : [...form.services, s]
        );

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            setStatus("error");
            setErrorMsg("Please fill in your name and email.");
            return;
        }
        setStatus("sending");
        setErrorMsg("");
        try {
            await axios.post(`${API}/contact`, form);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMsg(
                err?.response?.data?.detail?.[0]?.msg ||
                    err?.response?.data?.detail ||
                    "Something went wrong. Please email us directly at smartnetincca@gmail.com."
            );
        }
    };

    const reset = () => {
        setForm({
            name: "",
            email: "",
            company: "",
            phone: "",
            services: [],
            budget: "",
            message: "",
        });
        setStatus("idle");
    };

    return (
        <section id="contact" className="relative bg-navy-deep py-20 lg:py-28 overflow-hidden">
            <div className="absolute -top-40 left-1/3 w-[40rem] h-[40rem] rounded-full bg-gold/10 blur-[140px] pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2 lg:gap-20">
                <div>
                    <SectionHeading
                        dark
                        eyebrow="Contact"
                        title={
                            <>
                                Let's build your
                                <br />
                                growth engine.
                            </>
                        }
                    />
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-md text-mist leading-relaxed">
                            Tell us where you are and where you need to be.
                            We'll reply within one business day with next steps
                            and a free audit of your current funnel.
                        </p>
                    </Reveal>
                    <Reveal delay={0.25} className="mt-10 space-y-5">
                        <a
                            href="mailto:smartnetincca@gmail.com"
                            data-testid="contact-email-link"
                            className="flex items-center gap-4 group"
                        >
                            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gold/10 text-gold shrink-0">
                                <Mail size={19} />
                            </span>
                            <span className="text-white group-hover:text-gold transition-colors break-all">
                                smartnetincca@gmail.com
                            </span>
                        </a>
                        <div className="flex items-center gap-4">
                            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gold/10 text-gold shrink-0">
                                <MapPin size={19} />
                            </span>
                            <span className="text-white">
                                200 E Las Olas Blvd, Fort Lauderdale, FL 33301
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gold/10 text-gold shrink-0">
                                <Clock size={19} />
                            </span>
                            <span className="text-white">Replies within one business day</span>
                        </div>
                    </Reveal>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 36 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-9 backdrop-blur"
                >
                    {status === "success" ? (
                        <div
                            data-testid="contact-success-alert"
                            className="h-full min-h-[24rem] flex flex-col items-center justify-center text-center"
                        >
                            <CheckCircle2 size={56} className="text-gold" />
                            <h3 className="mt-6 font-display text-2xl font-bold text-white">
                                Message received
                            </h3>
                            <p className="mt-3 max-w-sm text-mist text-sm leading-relaxed">
                                Thank you — your request is in. A strategist
                                will get back to you within one business day.
                            </p>
                            <button
                                data-testid="contact-send-another-button"
                                onClick={reset}
                                className="mt-8 border border-white/25 text-white font-semibold px-6 py-3 rounded-full hover:border-gold hover:text-gold transition-colors"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form data-testid="contact-submission-form" onSubmit={onSubmit} className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-2">
                                        Full name *
                                    </label>
                                    <input
                                        data-testid="contact-input-name"
                                        className={inputCls}
                                        placeholder="Jane Cooper"
                                        value={form.name}
                                        onChange={(e) => set("name", e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-2">
                                        Work email *
                                    </label>
                                    <input
                                        data-testid="contact-input-email"
                                        type="email"
                                        className={inputCls}
                                        placeholder="jane@company.com"
                                        value={form.email}
                                        onChange={(e) => set("email", e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-2">
                                        Company
                                    </label>
                                    <input
                                        data-testid="contact-input-company"
                                        className={inputCls}
                                        placeholder="Company Inc."
                                        value={form.company}
                                        onChange={(e) => set("company", e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-2">
                                        Phone
                                    </label>
                                    <input
                                        data-testid="contact-input-phone"
                                        type="tel"
                                        className={inputCls}
                                        placeholder="+1 (555) 000-0000"
                                        value={form.phone}
                                        onChange={(e) => set("phone", e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-3">
                                    I'm interested in
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {SERVICE_OPTIONS.map((s) => {
                                        const active = form.services.includes(s);
                                        return (
                                            <button
                                                key={s}
                                                type="button"
                                                data-testid={`contact-service-chip-${s.toLowerCase().replace(/ /g, "-")}`}
                                                onClick={() => toggleService(s)}
                                                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                                                    active
                                                        ? "bg-gold text-navy border-gold"
                                                        : "border-white/20 text-mist hover:border-gold/60 hover:text-gold"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-2">
                                    Monthly growth budget
                                </label>
                                <select
                                    data-testid="contact-select-budget"
                                    className={`${inputCls} appearance-none bg-navy cursor-pointer`}
                                    value={form.budget}
                                    onChange={(e) => set("budget", e.target.value)}
                                >
                                    <option value="">Select a range…</option>
                                    {BUDGETS.map((b) => (
                                        <option key={b} value={b} className="bg-navy">
                                            {b}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-widest text-mist mb-2">
                                    Project brief
                                </label>
                                <textarea
                                    data-testid="contact-input-message"
                                    rows={4}
                                    className={`${inputCls} resize-none`}
                                    placeholder="What are you trying to grow, and what's getting in the way?"
                                    value={form.message}
                                    onChange={(e) => set("message", e.target.value)}
                                />
                            </div>

                            {status === "error" && (
                                <p
                                    data-testid="contact-error-alert"
                                    className="text-red-400 text-sm font-medium"
                                >
                                    {errorMsg}
                                </p>
                            )}

                            <button
                                data-testid="contact-form-submit-button"
                                type="submit"
                                disabled={status === "sending"}
                                className="btn-shine w-full inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold py-4 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
                            >
                                {status === "sending" ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        Request my free audit
                                        <ArrowUpRight size={18} />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
